import React, { useState } from "react";
import {
	alpha,
	Box,
	Button,
	Chip,
	IconButton,
	TableCell,
	TableRow,
	Tooltip,
	Typography,
} from "@mui/material";
import EmailTwoToneIcon from "@mui/icons-material/EmailTwoTone";
import PhoneIphoneTwoToneIcon from "@mui/icons-material/PhoneIphoneTwoTone";
import QrCodeScannerTwoToneIcon from "@mui/icons-material/QrCodeScannerTwoTone";
import ReceiptLongTwoToneIcon from "@mui/icons-material/ReceiptLongTwoTone";
import EditTwoToneIcon from "@mui/icons-material/EditTwoTone";
import DeleteTwoToneIcon from "@mui/icons-material/DeleteTwoTone";
import VisibilityTwoToneIcon from "@mui/icons-material/VisibilityTwoTone";
import DownloadTwoToneIcon from "@mui/icons-material/DownloadTwoTone";
import { Link as RouterLink } from "react-router-dom";
import type { ITicketListItem } from "../type/ticketScanType";
import PaymentDetailsModal from "./PaymentDetailsModal";
import { downloadTicketPassPNG } from "./TicketPassCard";

interface Props {
	ticket: ITicketListItem;
	index: number;
	paginationSkip: number | string;
	onScanClick: (ticket: ITicketListItem) => void;
	onEditClick?: (ticket: ITicketListItem) => void;
	onDeleteClick?: (ticket: ITicketListItem) => void;
}

const getStatusChip = (status: string) => {
	const normalized = (status || "").toLowerCase();
	if (normalized === "approved" || normalized === "paid") {
		return <Chip label={status || "Paid"} color="success" size="small" sx={{ fontWeight: 600 }} />;
	}
	if (normalized.includes("pending")) {
		return <Chip label={status || "Pending"} color="warning" size="small" sx={{ fontWeight: 600 }} />;
	}
	if (normalized === "rejected" || normalized === "unpaid") {
		return <Chip label={status || "Unpaid"} color="error" size="small" sx={{ fontWeight: 600 }} />;
	}
	return <Chip label={status || "Active"} color="default" size="small" variant="outlined" sx={{ fontWeight: 600 }} />;
};

const TicketListTableRow: React.FC<Props> = ({
	ticket,
	index,
	paginationSkip,
	onScanClick,
	onEditClick,
	onDeleteClick,
}) => {
	const [detailsModalOpen, setDetailsModalOpen] = useState(false);
	const visitorName = ticket.visitor_name || "Visitor";

	const hasPaymentDetails = Boolean(
		ticket.payment_details &&
		(typeof ticket.payment_details === "object"
			? Object.keys(ticket.payment_details).length > 0
			: Boolean(ticket.payment_details))
	);

	return (
		<TableRow hover key={ticket.id || index}>
			{/* SL */}
			<TableCell>
				<Typography variant="body2" color="text.secondary">
					{index + 1 + Number(paginationSkip)}
				</Typography>
			</TableCell>

			{/* Ticket Info */}
			<TableCell>
				<Chip
					component={RouterLink}
					to={`/visitor/ticket/${ticket.id}`}
					clickable
					label={ticket.ticket_no || `#${ticket.id}`}
					size="small"
					color="primary"
					variant="outlined"
					sx={{ fontWeight: 600, mb: 0.3, cursor: "pointer" }}
				/>
				{ticket.date && (
					<Typography
						variant="caption"
						sx={{
							display: "block",
							fontSize: "0.72rem",
							fontWeight: 600,
							color: "primary.main",
							mb: 0.2,
						}}
					>
						📅 {ticket.date}
					</Typography>
				)}

			</TableCell>

			{/* Visitor Info */}
			<TableCell>
				<Box sx={{ display: "flex", flexDirection: "column", gap: 0.4 }}>
					<Box sx={{ display: "flex", alignItems: "center", gap: 0.8, flexWrap: "wrap" }}>
						<Typography variant="body2" fontWeight={600} color="text.primary">
							{visitorName}
						</Typography>
						{ticket.visitor_no && (
							<Chip
								label={ticket.visitor_no}
								size="small"
								variant="outlined"
								sx={{
									height: 20,
									fontSize: "0.68rem",
									fontWeight: 600,
									borderRadius: "4px",
									borderColor: "grey.300",
									bgcolor: "grey.50",
								}}
							/>
						)}
					</Box>

					{(ticket.visitor_phone || ticket.visitor_email) && (
						<Box sx={{ display: "flex", flexDirection: "column", gap: 0.3, mt: 0.2 }}>
							{ticket.visitor_phone && (
								<Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}>
									<PhoneIphoneTwoToneIcon
										sx={{ fontSize: 13, color: "primary.main", opacity: 0.85 }}
									/>
									<Typography
										variant="caption"
										color="text.secondary"
										sx={{ fontSize: "0.75rem", lineHeight: 1.2 }}
									>
										{ticket.visitor_phone}
									</Typography>
								</Box>
							)}
							{ticket.visitor_email && (
								<Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}>
									<EmailTwoToneIcon
										sx={{ fontSize: 13, color: "primary.main", opacity: 0.85 }}
									/>
									<Typography

										color="text.secondary"
										sx={{
											fontSize: "0.75rem",
											lineHeight: 1.2,
											wordBreak: "break-word",
										}}
									>
										{ticket.visitor_email}
									</Typography>
								</Box>
							)}
						</Box>
					)}
				</Box>
			</TableCell>

			{/* Quantity & Pricing */}
			<TableCell>
				<Typography variant="body2" fontWeight={700} color="primary.main">
					{ticket.total_amount ? `${ticket.total_amount}` : "-"}
				</Typography>
				<Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
					Qty: {ticket.quantity || 1}
				</Typography>
			</TableCell>

			{/* Payment Details */}
			<TableCell>
				<Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-start", gap: 0.6 }}>
					<Box sx={{ display: "flex", alignItems: "center", gap: 0.6 }}>
						{getStatusChip(ticket.payment_status || "")}
						{hasPaymentDetails && (
							<Tooltip title="View Payment Details" arrow>
								<IconButton
									size="small"
									onClick={() => setDetailsModalOpen(true)}
									sx={{
										p: 0.3,
										color: "primary.main",
										bgcolor: (theme) => alpha(theme.palette.primary.main, 0.08),
										"&:hover": {
											bgcolor: (theme) => alpha(theme.palette.primary.main, 0.18),
										},
										borderRadius: 1,
									}}
								>
									<ReceiptLongTwoToneIcon sx={{ fontSize: 16 }} />
								</IconButton>
							</Tooltip>
						)}
					</Box>

					{ticket.payment_method && (
						<Chip
							label={ticket.payment_method}
							size="small"
							variant="outlined"
							sx={{
								height: 20,
								fontSize: "0.7rem",
								fontWeight: 600,
								textTransform: "capitalize",
								borderColor: "grey.300",
								bgcolor: "grey.50",
							}}
						/>
					)}

					{ticket.payment_details?.note && (
						<Typography
							variant="caption"
							color="text.secondary"
							sx={{
								display: "block",
								fontSize: "0.72rem",
								fontStyle: "italic",
								lineHeight: 1.2,
								maxWidth: 160,
								wordBreak: "break-word",
							}}
						>
							Note: {ticket.payment_details.note}
						</Typography>
					)}
				</Box>

				{detailsModalOpen && (
					<PaymentDetailsModal
						open={detailsModalOpen}
						handleClose={() => setDetailsModalOpen(false)}
						ticket={ticket}
					/>
				)}
			</TableCell>
			<TableCell>
				<Tooltip title="Download Pass PNG" arrow>
					<IconButton
						size="small"
						color="success"
						onClick={() => downloadTicketPassPNG(ticket)}
						sx={{
							p: 0.7,
							borderRadius: 1.5,
							border: (theme) =>
								`1px solid ${alpha(theme.palette.success.main, 0.3)}`,
							bgcolor: (theme) =>
								alpha(theme.palette.success.main, 0.05),
							"&:hover": {
								bgcolor: (theme) =>
									alpha(theme.palette.success.main, 0.15),
							},
						}}
					>
						<DownloadTwoToneIcon sx={{ fontSize: 16 }} />
					</IconButton>
				</Tooltip>
			</TableCell>

			{/* Action */}
			<TableCell align="center">
				<Box
					sx={{
						display: "flex",
						alignItems: "center",
						justifyContent: "center",
						gap: 0.8,
					}}
				>
					<Tooltip title="View Ticket Details" arrow>
						<IconButton
							component={RouterLink}
							to={`/visitor/ticket/${ticket.id}`}
							size="small"
							color="primary"
							sx={{
								p: 0.7,
								borderRadius: 1.5,
								border: (theme) =>
									`1px solid ${alpha(theme.palette.primary.main, 0.3)}`,
								bgcolor: (theme) =>
									alpha(theme.palette.primary.main, 0.05),
								"&:hover": {
									bgcolor: (theme) =>
										alpha(theme.palette.primary.main, 0.15),
								},
							}}
						>
							<VisibilityTwoToneIcon sx={{ fontSize: 16 }} />
						</IconButton>
					</Tooltip>



					{onEditClick && (
						<Tooltip title="Update Ticket" arrow>
							<IconButton
								size="small"
								color="primary"
								onClick={() => onEditClick(ticket)}
								sx={{
									p: 0.7,
									borderRadius: 1.5,
									border: (theme) =>
										`1px solid ${alpha(theme.palette.primary.main, 0.3)}`,
									bgcolor: (theme) =>
										alpha(theme.palette.primary.main, 0.05),
									"&:hover": {
										bgcolor: (theme) =>
											alpha(theme.palette.primary.main, 0.15),
									},
								}}
							>
								<EditTwoToneIcon sx={{ fontSize: 16 }} />
							</IconButton>
						</Tooltip>
					)}

					<Button
						size="small"
						variant="outlined"
						color="primary"
						startIcon={<QrCodeScannerTwoToneIcon fontSize="small" />}
						onClick={() => onScanClick(ticket)}
						sx={{
							borderRadius: 1.5,
							textTransform: "none",
							fontWeight: 600,
							whiteSpace: "nowrap",
						}}
					>
						Scan
					</Button>

					{onDeleteClick && (
						<Tooltip title="Delete Ticket" arrow>
							<IconButton
								size="small"
								color="error"
								onClick={() => onDeleteClick(ticket)}
								sx={{
									p: 0.7,
									borderRadius: 1.5,
									border: (theme) =>
										`1px solid ${alpha(theme.palette.error.main, 0.3)}`,
									bgcolor: (theme) =>
										alpha(theme.palette.error.main, 0.05),
									"&:hover": {
										bgcolor: (theme) =>
											alpha(theme.palette.error.main, 0.15),
									},
								}}
							>
								<DeleteTwoToneIcon sx={{ fontSize: 16 }} />
							</IconButton>
						</Tooltip>
					)}
				</Box>
			</TableCell>
		</TableRow>
	);
};

export default TicketListTableRow;

